import React, { useEffect, useMemo, useRef, useState } from "react";
import { SwipeRow } from "./SwipeRow";
import {
  addGroupMember,
  createExpenseComment,
  createCategory,
  createGroupExpense,
  createGroupInvite,
  createGroupSettlement,
  deleteExpenseComment,
  deleteGroupExpense,
  deleteGroupSettlement,
  leaveGroup,
  listGroupInvites,
  removeGroupMember,
  revokeGroupInvite,
  updateGroup,
  getFeatures,
  getGroup,
  getGroupBalances,
  getGroupExpense,
  listCategories,
  listContacts,
  listExpenseAttachments,
  listGroupExpenses,
  listGroupMembers,
  listGroupSettlements,
  listSettlementAttachments,
  openAuthedFile,
  restoreExpenseComment,
  restoreGroupExpense,
  restoreGroupSettlement,
  updateExpenseComment,
  updateGroupExpense,
  uploadFile,
  uploadExpenseAttachment,
  uploadSettlementAttachment,
  type CategoryResponse,
  type ContactResponse,
  type ExpenseAttachmentResponse,
  type ExpenseDetailResponse,
  type ExpenseResponse,
  type FeaturesResponse,
  type GroupInviteResponse,
  type GroupBalancesResponse,
  type GroupMemberAddRequest,
  type GroupMemberResponse,
  type GroupResponse,
  type SettlementAttachmentResponse,
  type SettlementResponse,
} from "./api";
import { AuthedImage } from "./AuthedImage";
import { datetimeLocalNowValue, datetimeLocalToIso, formatMoney, isoToDatetimeLocalValue, parseMoneyToMinor } from "./money";
import { t, type Lang } from "./i18n";
import { useIsMobileTouch } from "./useIsMobileTouch";

type Props = {
  groupId: string;
  meId: string | null;
  lang: Lang;
  currencyOptions: string[];
  onNavigate: (path: string) => void;
  onBalanceUpdated?: (groupId: string, currency: string, netMinor: number) => void;
  triggerNewExpense?: number;
};

type SplitType = "equal" | "exact" | "percent" | "shares";

function distributeMinorEqually(totalMinor: number, selectedUserIdsInOrder: string[]): Record<string, number> {
  if (totalMinor <= 0 || selectedUserIdsInOrder.length === 0) return {};
  const base = Math.floor(totalMinor / selectedUserIdsInOrder.length);
  const remainder = totalMinor % selectedUserIdsInOrder.length;
  const distribution: Record<string, number> = {};
  selectedUserIdsInOrder.forEach((userId, index) => {
    distribution[userId] = base + (index < remainder ? 1 : 0);
  });
  return distribution;
}

export function GroupPage({ groupId, meId, lang, currencyOptions, onNavigate, onBalanceUpdated, triggerNewExpense = 0 }: Props) {
  const cardStyle: React.CSSProperties = {
    background: "var(--swk-card)",
    borderRadius: 14,
    boxShadow: "0 8px 18px rgba(0,0,0,0.04)",
    padding: 12,
  };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid var(--swk-border)",
    background: "var(--swk-input-bg)",
    color: "var(--swk-input-text)",
  };
  const outlineButton: React.CSSProperties = {
    padding: "10px 14px",
    background: "var(--swk-input-bg)",
    color: "#0f8c57",
    borderRadius: 10,
    border: "1px solid var(--swk-green)",
    fontWeight: 700,
  };
  const primaryButton: React.CSSProperties = {
    padding: "10px 14px",
    background: "#0f8c57",
    color: "#fff",
    borderRadius: 10,
    border: "1px solid #0f8c57",
    fontWeight: 700,
  };
  const smallButton: React.CSSProperties = {
    padding: "6px 10px",
    background: "var(--swk-input-bg)",
    color: "#0f8c57",
    borderRadius: 10,
    border: "1px solid var(--swk-border)",
    fontWeight: 700,
  };
  const smallDangerButton: React.CSSProperties = {
    padding: "6px 10px",
    background: "var(--swk-input-bg)",
    color: "#c65a2b",
    borderRadius: 10,
    border: "1px solid #f2d2c2",
    fontWeight: 700,
  };
  const dangerOutlineButton: React.CSSProperties = {
    padding: "10px 14px",
    background: "var(--swk-input-bg)",
    color: "#c65a2b",
    borderRadius: 10,
    border: "1px solid #f2d2c2",
    fontWeight: 800,
  };
  const iconButton: React.CSSProperties = {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid var(--swk-border)",
    background: "var(--swk-card)",
    color: "var(--swk-input-text)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  };
  const subtleText: React.CSSProperties = { fontSize: 12, color: "var(--swk-muted)" };

  const [group, setGroup] = useState<GroupResponse | null>(null);
  const [members, setMembers] = useState<GroupMemberResponse[] | null>(null);
  const [features, setFeatures] = useState<FeaturesResponse>({
    debt_simplification: true,
    comments: true,
    attachments: true,
  });
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editAvatarId, setEditAvatarId] = useState<string | null>(null);
  const [balances, setBalances] = useState<GroupBalancesResponse | null>(null);
  const [expenses, setExpenses] = useState<ExpenseResponse[] | null>(null);
  const [settlements, setSettlements] = useState<SettlementResponse[] | null>(null);
  const [categories, setCategories] = useState<CategoryResponse[] | null>(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [invites, setInvites] = useState<GroupInviteResponse[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [mode, setMode] = useState<"view" | "new-expense" | "edit-expense" | "new-settlement" | "edit-settlement">("view");
  const [activeExpenseId, setActiveExpenseId] = useState<string | null>(null);
  const [activeExpense, setActiveExpense] = useState<ExpenseDetailResponse | null>(null);
  const [activeSettlementId, setActiveSettlementId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberMode, setAddMemberMode] = useState<"contacts" | "email">("contacts");
  const [contacts, setContacts] = useState<ContactResponse[] | null>(null);
  const [contactsStatus, setContactsStatus] = useState<string | null>(null);
  const [selectedContactUserIds, setSelectedContactUserIds] = useState<Record<string, boolean>>({});
  const [addMemberEmail, setAddMemberEmail] = useState("");
  const [addMemberDisplayName, setAddMemberDisplayName] = useState("");
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [addingMember, setAddingMember] = useState(false);

  const memberById = useMemo(() => {
    const m = new Map<string, GroupMemberResponse>();
    for (const x of members ?? []) m.set(x.user_id, x);
    return m;
  }, [members]);
  const isAdmin = useMemo(() => members?.some((m) => m.user_id === meId && m.role === "admin") ?? false, [members, meId]);
  const friendDisplayName = useMemo(() => {
    if (group?.kind !== "friendship") return null;
    const other = members?.find((m) => m.user_id !== meId) || members?.[0];
    return other?.display_name || other?.email || group?.name || null;
  }, [group?.kind, group?.name, meId, members]);

  const categoryById = useMemo(() => {
    const m = new Map<string, CategoryResponse>();
    for (const c of categories ?? []) m.set(c.id, c);
    return m;
  }, [categories]);

  const currency = group?.currency_code ?? "RUB";
  const isMobileTouch = useIsMobileTouch();
  const [openExpenseSwipe, setOpenExpenseSwipe] = useState<{ id: string; side: "left" | "right" } | null>(null);
  const [openSettlementSwipe, setOpenSettlementSwipe] = useState<{ id: string; side: "left" | "right" } | null>(null);
  const myNetMinor = useMemo(() => {
    if (!meId) return null;
    return balances?.balances.find((b) => b.user_id === meId)?.net_minor ?? null;
  }, [balances?.balances, meId]);

  async function refreshAll() {
    setStatus(null);
    try {
      const [f, g, mem, bal, exp, setl, cats] = await Promise.all([
        getFeatures(),
        getGroup(groupId),
        listGroupMembers(groupId),
        getGroupBalances(groupId),
        listGroupExpenses(groupId, includeDeleted),
        listGroupSettlements(groupId, includeDeleted),
        listCategories(),
      ]);
      setFeatures(f);
      setGroup(g);
      setMembers(mem);
      setBalances(bal);
      setExpenses(exp);
      setSettlements(setl);
      setCategories(cats);
      setOpenExpenseSwipe(null);
      setOpenSettlementSwipe(null);
      if (meId) {
        const mine = bal.balances.find((b) => b.user_id === meId)?.net_minor ?? 0;
        onBalanceUpdated?.(groupId, bal.currency_code, mine);
      }
      if (g.kind === "group" && mem.some((m) => m.user_id === meId && m.role === "admin")) {
        try {
          const inv = await listGroupInvites(groupId);
          setInvites(inv);
        } catch {
          setInvites(null);
        }
      } else {
        setInvites(null);
      }
    } catch (e) {
      setStatus(String(e));
    }
  }

  async function refreshExpense(expenseId: string) {
    const d = await getGroupExpense(groupId, expenseId);
    setActiveExpense(d);
  }

  async function ensureContactsLoaded() {
    if (contacts || contactsStatus) return;
    try {
      const all = await listContacts();
      // Include all contacts (including backend-generated virtual ones) for member picking.
      setContacts(all ?? []);
    } catch (e) {
      setContactsStatus(String(e));
      setContacts([]);
    }
  }

  const eligibleContacts = useMemo(() => {
    const existing = new Set((members ?? []).map((m) => m.user_id));
    const query = addMemberSearch.trim().toLowerCase();
    const base = (contacts ?? [])
      .filter((c) => Boolean(c.contact_user_id))
      .filter((c) => !existing.has(c.contact_user_id || ""))
      .filter((c) => (c.contact_user_id || "") !== (meId || ""))
      .sort((a, b) => (a.resolved_display_name || "").localeCompare(b.resolved_display_name || ""));
    if (!query) return base;
    return base.filter((c) => `${c.resolved_display_name || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase().includes(query));
  }, [addMemberSearch, contacts, members, meId]);

  function openAddMemberDialog() {
    setShowAddMember(true);
    setAddMemberMode("contacts");
    setContacts(null);
    setContactsStatus(null);
    setSelectedContactUserIds({});
    setAddMemberEmail("");
    setAddMemberDisplayName("");
    setAddMemberSearch("");
    ensureContactsLoaded().catch(() => undefined);
  }

  async function onAddMembersToGroup() {
    if (!group || group.kind !== "group") return;
    if (!isAdmin) return;
    if (addingMember) return;

    setAddingMember(true);
    setStatus(null);
    try {
      if (addMemberMode === "contacts") {
        const ids = Object.entries(selectedContactUserIds)
          .filter(([, v]) => Boolean(v))
          .map(([k]) => k);
        if (!ids.length) throw new Error("No contacts selected");
        for (const id of ids) {
          const payload: GroupMemberAddRequest = { user_id: id };
          await addGroupMember(groupId, payload);
        }
      } else {
        const email = addMemberEmail.trim();
        if (!email) throw new Error("Email is required");
        const displayName = addMemberDisplayName.trim();
        const payload: GroupMemberAddRequest = {
          email,
          display_name: displayName ? displayName : null,
        };
        await addGroupMember(groupId, payload);
      }

      const mem = await listGroupMembers(groupId);
      setMembers(mem);

      // Reset UI
      setSelectedContactUserIds({});
      setAddMemberEmail("");
      setAddMemberDisplayName("");
      setAddMemberSearch("");
      setShowAddMember(false);
    } catch (e) {
      setStatus(String(e));
    } finally {
      setAddingMember(false);
    }
  }

  useEffect(() => {
    refreshAll().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, includeDeleted]);

  useEffect(() => {
    if (!group) return;
    setEditName(group.name);
    setEditDescription(group.description || "");
    setEditAvatarId(group.avatar_attachment_id || null);
    setEditMode(false);
  }, [group]);

  useEffect(() => {
    if (!group || !isAdmin || group.kind !== "group") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("edit") !== "1") return;
    setEditMode(true);
    params.delete("edit");
    const query = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  }, [group, isAdmin]);

  useEffect(() => {
    if (mode === "edit-expense" && activeExpenseId) {
      refreshExpense(activeExpenseId).catch((e) => setStatus(String(e)));
    } else {
      setActiveExpense(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeExpenseId, mode]);

  function label(uid: string) {
    const m = memberById.get(uid);
    return m ? m.display_name || m.email : uid;
  }

  async function onCreateGroupInvite() {
    setStatus(null);
    try {
      const inv = await createGroupInvite(groupId);
      setInviteUrl(inv.url ?? null);
      if (group?.kind === "group" && isAdmin) {
        const list = await listGroupInvites(groupId);
        setInvites(list);
      }
    } catch (e) {
      setStatus(String(e));
    }
  }

  function exportCsv() {
    if (!expenses || !settlements) return;
    setExporting(true);
    try {
      const rows: string[] = [];
      rows.push("type,id,description,amount,currency,occurred_at,from,to,category,note");
      for (const e of expenses) {
        const cat = e.category_id ? categoryById.get(e.category_id)?.name ?? e.category_id : "";
        rows.push(
          [
            "expense",
            e.id,
            JSON.stringify(e.description),
            (e.amount_minor / 100).toFixed(2),
            e.currency_code,
            e.occurred_at,
            "",
            "",
            JSON.stringify(cat),
            JSON.stringify(e.note ?? ""),
          ].join(","),
        );
      }
      for (const s of settlements) {
        rows.push(
          [
            "settlement",
            s.id,
            JSON.stringify("settlement"),
            (s.amount_minor / 100).toFixed(2),
            s.currency_code,
            s.created_at,
            s.from_user_id,
            s.to_user_id,
            "",
            JSON.stringify(s.note ?? ""),
          ].join(","),
        );
      }
      const blob = new Blob([rows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `group-${groupId}-export.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  async function onLeaveGroup() {
    if (myNetMinor !== null && myNetMinor !== 0) {
      alert(t(lang, "cannotLeaveGroupBalance"));
      return;
    }
    if (!confirm(t(lang, "leaveGroupConfirm"))) return;
    try {
      await leaveGroup(groupId);
      onNavigate("/groups");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("Cannot leave group with non-zero balance")) {
        alert(t(lang, "cannotLeaveGroupBalance"));
        return;
      }
      alert(String(e));
    }
  }

  async function onRemoveMember(memberUserId: string) {
    if (!confirm(t(lang, "removeMember") + "?")) return;
    try {
      await removeGroupMember(groupId, memberUserId);
      await refreshAll();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("Cannot remove member with non-zero balance")) {
        alert(t(lang, "cannotRemoveMemberBalance"));
        return;
      }
      if (msg.includes("Cannot remove last admin")) {
        alert(t(lang, "cannotRemoveLastAdmin"));
        return;
      }
      if (msg.includes("Use leave endpoint")) {
        alert(t(lang, "useLeaveEndpoint"));
        return;
      }
      alert(String(e));
    }
  }

  async function onRevokeInvite(token: string) {
    if (!confirm(t(lang, "revoke") + "?")) return;
    try {
      await revokeGroupInvite(groupId, token);
      const list = await listGroupInvites(groupId);
      setInvites(list);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("Invite already used")) {
        alert(t(lang, "inviteAlreadyUsed"));
        return;
      }
      alert(String(e));
    }
  }

  const netByUserId = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of balances?.balances ?? []) m.set(b.user_id, b.net_minor);
    return m;
  }, [balances?.balances]);

  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseAmountMajor, setExpenseAmountMajor] = useState("0");
  const [expenseCurrencyCode, setExpenseCurrencyCode] = useState(currency);
  const [expenseDatetimeLocal, setExpenseDatetimeLocal] = useState(datetimeLocalNowValue());
  const [expenseNote, setExpenseNote] = useState("");
  const [expenseCategoryId, setExpenseCategoryId] = useState<string>("");
  const [splitType, setSplitType] = useState<SplitType>("equal");
  const [newCategoryName, setNewCategoryName] = useState("");

  const [payerAmounts, setPayerAmounts] = useState<Record<string, string>>({});
  const [equalPayers, setEqualPayers] = useState<Record<string, boolean>>({});
  const [equalParticipants, setEqualParticipants] = useState<Record<string, boolean>>({});
  const [exactAmounts, setExactAmounts] = useState<Record<string, string>>({});
  const [percentValues, setPercentValues] = useState<Record<string, string>>({});
  const [sharesValues, setSharesValues] = useState<Record<string, string>>({});

  const amountMinor = parseMoneyToMinor(expenseAmountMajor || "0");
  const selectedEqualMemberIds = useMemo(() => {
    const all = members ?? [];
    if (group?.kind === "friendship") return all.map((m) => m.user_id);
    return all.filter((m) => equalParticipants[m.user_id] ?? true).map((m) => m.user_id);
  }, [equalParticipants, group?.kind, members]);
  const selectedEqualPayerIds = useMemo(() => {
    const all = members ?? [];
    return all.filter((m) => equalPayers[m.user_id] ?? false).map((m) => m.user_id);
  }, [equalPayers, members]);
  const equalAutoPayerMinorByUser = useMemo(
    () => distributeMinorEqually(amountMinor, selectedEqualPayerIds),
    [amountMinor, selectedEqualPayerIds],
  );
  const payerRemaining = useMemo(() => {
    if (splitType === "equal") return 0;
    const sum = Object.values(payerAmounts).reduce((acc, v) => acc + parseMoneyToMinor(v || "0"), 0);
    return amountMinor - sum;
  }, [payerAmounts, amountMinor, splitType]);
  const exactRemaining = useMemo(() => {
    if (splitType !== "exact") return 0;
    const sum = Object.values(exactAmounts).reduce((acc, v) => acc + parseMoneyToMinor(v || "0"), 0);
    return amountMinor - sum;
  }, [exactAmounts, amountMinor, splitType]);

  function resetExpenseForm() {
    setExpenseDescription("");
    setExpenseAmountMajor("0");
    setExpenseCurrencyCode(currency);
    setExpenseDatetimeLocal(datetimeLocalNowValue());
    setExpenseNote("");
    setExpenseCategoryId("");
    setSplitType("equal");
    setPayerAmounts({});
    setEqualPayers({});
    setEqualParticipants({});
    setExactAmounts({});
    setPercentValues({});
    setSharesValues({});
    setPendingNewExpenseAttachment(null);
  }

  const lastExpenseTrigger = useRef(triggerNewExpense);

  useEffect(() => {
    if (triggerNewExpense === lastExpenseTrigger.current) return;
    lastExpenseTrigger.current = triggerNewExpense;
    setMode("new-expense");
    setActiveExpenseId(null);
    resetExpenseForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerNewExpense]);

  useEffect(() => {
    if (!members) return;
    if (!meId) return;
    if (mode !== "new-expense") return;
    if (Object.keys(payerAmounts).length === 0) {
      setPayerAmounts({ [meId]: expenseAmountMajor });
    }
    if (Object.keys(equalParticipants).length === 0) {
      const m: Record<string, boolean> = {};
      for (const x of members) m[x.user_id] = true;
      setEqualParticipants(m);
    }
    if (Object.keys(equalPayers).length === 0) {
      const m: Record<string, boolean> = {};
      for (const x of members) m[x.user_id] = x.user_id === meId;
      if (!m[meId] && members.length > 0) {
        m[members[0].user_id] = true;
      }
      setEqualPayers(m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, members, meId]);

  useEffect(() => {
    if (mode !== "view") return;
    setExpenseCurrencyCode(currency);
    setSettlementCurrencyCode(currency);
  }, [currency, mode]);

  async function submitExpense(isEdit: boolean) {
    if (!members) throw new Error("Members not loaded");
    const amountMinor = parseMoneyToMinor(expenseAmountMajor);
    if (amountMinor <= 0) throw new Error("Amount must be > 0");

    let payers: Array<{ user_id: string; amount_minor: number }>;
    if (splitType === "equal") {
      if (selectedEqualMemberIds.length === 0) throw new Error(t(lang, "selectAtLeastOneParticipant"));
      if (selectedEqualPayerIds.length === 0) throw new Error(t(lang, "selectAtLeastOnePayer"));
      const autoPayers = distributeMinorEqually(amountMinor, selectedEqualPayerIds);
      payers = selectedEqualPayerIds
        .map((user_id) => ({ user_id, amount_minor: autoPayers[user_id] ?? 0 }))
        .filter((x) => x.amount_minor > 0);
    } else {
      payers = Object.entries(payerAmounts)
        .map(([user_id, v]) => ({ user_id, amount_minor: parseMoneyToMinor(v || "0") }))
        .filter((x) => x.amount_minor > 0);
      const payerSum = payers.reduce((a, x) => a + x.amount_minor, 0);
      if (payerSum !== amountMinor) throw new Error("Payers sum must equal amount");
    }

    const payload: any = {
      description: expenseDescription,
      amount_minor: amountMinor,
      currency_code: expenseCurrencyCode.trim().toUpperCase(),
      occurred_at: datetimeLocalToIso(expenseDatetimeLocal),
      split_type: splitType,
      category_id: expenseCategoryId || null,
      note: expenseNote || null,
      payers,
    };

    if (splitType === "equal") {
      payload.split_equal_user_ids = selectedEqualMemberIds;
    } else if (splitType === "exact") {
      const split_exact = members
        .map((m) => ({ user_id: m.user_id, amount_minor: parseMoneyToMinor(exactAmounts[m.user_id] || "0") }))
        .filter((x) => x.amount_minor !== 0);
      const sum = split_exact.reduce((a, x) => a + x.amount_minor, 0);
      if (sum !== amountMinor) throw new Error("Exact splits sum must equal amount");
      payload.split_exact = split_exact;
    } else if (splitType === "percent") {
      const split_percent = members
        .map((m) => {
          const v = Number((percentValues[m.user_id] || "0").replace(",", "."));
          const bp = Math.round(v * 100);
          return { user_id: m.user_id, percent_bp: bp };
        })
        .filter((x) => x.percent_bp !== 0);
      const sum = split_percent.reduce((a, x) => a + x.percent_bp, 0);
      if (sum !== 10000) throw new Error("Percent must sum to 100.00");
      payload.split_percent = split_percent;
    } else if (splitType === "shares") {
      const split_shares = members
        .map((m) => ({ user_id: m.user_id, shares: Math.max(0, Math.floor(Number(sharesValues[m.user_id] || "0"))) }))
        .filter((x) => x.shares !== 0);
      const sum = split_shares.reduce((a, x) => a + x.shares, 0);
      if (sum <= 0) throw new Error("Shares must sum to > 0");
      payload.split_shares = split_shares;
    }

    if (isEdit) {
      if (!activeExpenseId) throw new Error("No expense selected");
      await updateGroupExpense(groupId, activeExpenseId, payload);
    } else {
      if (pendingNewExpenseAttachment && pendingNewExpenseAttachment.type.startsWith("image/") && pendingNewExpenseAttachment.size > MAX_RECEIPT_IMAGE_BYTES) {
        throw new Error("Image too large (max 5 MB)");
      }
      const created = await createGroupExpense(groupId, payload);
      if (pendingNewExpenseAttachment) {
        try {
          await uploadExpenseAttachment(groupId, created.id, pendingNewExpenseAttachment);
        } catch (e) {
          // Expense is created already; keep user in edit mode so they can retry attaching.
          setStatus(`Attachment upload failed: ${String(e)}`);
          beginEditExpense(created);
          setPendingNewExpenseAttachment(null);
          return;
        }
      }
    }
    await refreshAll();
    setMode("view");
    setActiveExpenseId(null);
    resetExpenseForm();
  }

  function beginEditExpense(e: ExpenseResponse) {
    setMode("edit-expense");
    setActiveExpenseId(e.id);
    setExpenseDescription(e.description);
    setExpenseAmountMajor((e.amount_minor / 100).toFixed(2));
    setExpenseCurrencyCode((e.currency_code || currency).toUpperCase());
    setExpenseDatetimeLocal(isoToDatetimeLocalValue(e.occurred_at));
    setExpenseNote(e.note ?? "");
    setExpenseCategoryId(e.category_id ?? "");
    setSplitType(e.split_type as SplitType);
    const p: Record<string, string> = {};
    for (const x of e.payers) p[x.user_id] = (x.amount_minor / 100).toFixed(2);
    setPayerAmounts(p);

    const payerSelection: Record<string, boolean> = {};
    for (const x of members ?? []) payerSelection[x.user_id] = false;
    for (const x of e.payers) payerSelection[x.user_id] = true;
    if (!Object.values(payerSelection).some(Boolean) && (members ?? []).length > 0) {
      payerSelection[(members ?? [])[0].user_id] = true;
    }
    setEqualPayers(payerSelection);

    setEqualParticipants({});
    setExactAmounts({});
    setPercentValues({});
    setSharesValues({});

    if (e.split_type === "equal") {
      const selected = new Set(e.splits.map((s) => s.user_id));
      const m: Record<string, boolean> = {};
      for (const x of members ?? []) m[x.user_id] = selected.has(x.user_id);
      setEqualParticipants(m);
    } else if (e.split_type === "exact") {
      const m: Record<string, string> = {};
      for (const x of e.splits) m[x.user_id] = (x.amount_minor / 100).toFixed(2);
      setExactAmounts(m);
    } else if (e.split_type === "percent") {
      const total = e.amount_minor || 1;
      const m: Record<string, string> = {};
      for (const x of e.splits) {
        const pct = (x.amount_minor / total) * 100;
        m[x.user_id] = pct.toFixed(2);
      }
      setPercentValues(m);
    } else if (e.split_type === "shares") {
      const nonZero = e.splits.map((s) => s.amount_minor).filter((v) => v > 0);
      const min = nonZero.length ? Math.min(...nonZero) : 1;
      const m: Record<string, string> = {};
      for (const x of e.splits) m[x.user_id] = String(Math.max(0, Math.round(x.amount_minor / min)));
      setSharesValues(m);
    }
  }

  const [settlementFrom, setSettlementFrom] = useState("");
  const [settlementTo, setSettlementTo] = useState("");
  const [settlementAmountMajor, setSettlementAmountMajor] = useState("0");
  const [settlementCurrencyCode, setSettlementCurrencyCode] = useState(currency);
  const [settlementNote, setSettlementNote] = useState("");
  const availableCurrencyOptions = useMemo(() => {
    const normalized = currencyOptions.map((c) => c.trim().toUpperCase()).filter((c) => c.length === 3);
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const item of normalized) {
      if (seen.has(item)) continue;
      seen.add(item);
      ordered.push(item);
    }
    for (const fallback of [currency, expenseCurrencyCode, settlementCurrencyCode]) {
      const item = fallback.trim().toUpperCase();
      if (item.length !== 3 || seen.has(item)) continue;
      seen.add(item);
      ordered.push(item);
    }
    return ordered;
  }, [currency, currencyOptions, expenseCurrencyCode, settlementCurrencyCode]);

  function resetSettlementForm() {
    setSettlementFrom("");
    setSettlementTo("");
    setSettlementAmountMajor("0");
    setSettlementCurrencyCode(currency);
    setSettlementNote("");
    setActiveSettlementId(null);
  }

  function beginEditSettlement(s: SettlementResponse) {
    setMode("edit-settlement");
    setActiveSettlementId(s.id);
    setSettlementFrom(s.from_user_id);
    setSettlementTo(s.to_user_id);
    setSettlementAmountMajor((s.amount_minor / 100).toFixed(2));
    setSettlementCurrencyCode((s.currency_code || currency).toUpperCase());
    setSettlementNote(s.note ?? "");
  }

  async function submitSettlement(isEdit: boolean) {
    const amountMinor = parseMoneyToMinor(settlementAmountMajor);
    if (amountMinor <= 0) throw new Error("Amount must be > 0");
    if (!settlementFrom || !settlementTo) throw new Error("Select users");
    if (settlementFrom === settlementTo) throw new Error("Users must differ");
    const payload = {
      from_user_id: settlementFrom,
      to_user_id: settlementTo,
      amount_minor: amountMinor,
      currency_code: settlementCurrencyCode.trim().toUpperCase(),
      note: settlementNote || null,
    };
    if (isEdit) {
      if (!activeSettlementId) throw new Error("No settlement selected");
      await deleteGroupSettlement(groupId, activeSettlementId);
      try {
        await createGroupSettlement(groupId, payload);
      } catch (e) {
        await restoreGroupSettlement(groupId, activeSettlementId).catch(() => undefined);
        throw e;
      }
    } else {
      await createGroupSettlement(groupId, payload);
    }
    await refreshAll();
    setMode("view");
    resetSettlementForm();
  }

  async function onDeleteExpense(expenseId: string) {
    setOpenExpenseSwipe(null);
    await deleteGroupExpense(groupId, expenseId);
    await refreshAll();
  }

  async function onRestoreExpense(expenseId: string) {
    setOpenExpenseSwipe(null);
    await restoreGroupExpense(groupId, expenseId);
    await refreshAll();
  }

  async function onDeleteSettlement(settlementId: string) {
    setOpenSettlementSwipe(null);
    await deleteGroupSettlement(groupId, settlementId);
    await refreshAll();
  }

  async function onRestoreSettlement(settlementId: string) {
    setOpenSettlementSwipe(null);
    await restoreGroupSettlement(groupId, settlementId);
    await refreshAll();
  }

  const MAX_RECEIPT_IMAGE_BYTES = 5 * 1024 * 1024;

  const [newComment, setNewComment] = useState("");
  const [editCommentId, setEditCommentId] = useState<string | null>(null);
  const [editCommentBody, setEditCommentBody] = useState("");
  const [attachments, setAttachments] = useState<ExpenseAttachmentResponse[]>([]);
  const [pendingNewExpenseAttachment, setPendingNewExpenseAttachment] = useState<File | null>(null);
  const [isUploadingExpenseAttachment, setIsUploadingExpenseAttachment] = useState(false);
  const [settlementAttachmentsOpenId, setSettlementAttachmentsOpenId] = useState<string | null>(null);
  const [settlementAttachments, setSettlementAttachments] = useState<SettlementAttachmentResponse[]>([]);

  async function refreshAttachments(expenseId: string) {
    const items = await listExpenseAttachments(groupId, expenseId);
    setAttachments(items);
  }

  useEffect(() => {
    if (!features.attachments) {
      setAttachments([]);
      return;
    }
    if (mode === "edit-expense" && activeExpenseId) {
      refreshAttachments(activeExpenseId).catch(() => undefined);
    } else {
      setAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeExpenseId, features.attachments, mode]);

  async function onAddComment() {
    if (!activeExpenseId) return;
    const c = await createExpenseComment(groupId, activeExpenseId, newComment);
    setNewComment("");
    await refreshExpense(activeExpenseId);
    return c;
  }

  async function onSaveComment() {
    if (!activeExpenseId || !editCommentId) return;
    await updateExpenseComment(groupId, activeExpenseId, editCommentId, editCommentBody);
    setEditCommentId(null);
    setEditCommentBody("");
    await refreshExpense(activeExpenseId);
  }

  async function onDeleteComment(commentId: string) {
    if (!activeExpenseId) return;
    await deleteExpenseComment(groupId, activeExpenseId, commentId);
    await refreshExpense(activeExpenseId);
  }

  async function onRestoreComment(commentId: string) {
    if (!activeExpenseId) return;
    await restoreExpenseComment(groupId, activeExpenseId, commentId);
    await refreshExpense(activeExpenseId);
  }

  async function onUploadAttachment(file: File) {
    if (file.type.startsWith("image/") && file.size > MAX_RECEIPT_IMAGE_BYTES) {
      throw new Error("Image too large (max 5 MB)");
    }

    if (mode === "new-expense") {
      setPendingNewExpenseAttachment(file);
      return;
    }

    if (!activeExpenseId) return;
    setIsUploadingExpenseAttachment(true);
    try {
      await uploadExpenseAttachment(groupId, activeExpenseId, file);
      await refreshAttachments(activeExpenseId);
    } finally {
      setIsUploadingExpenseAttachment(false);
    }
  }

  async function refreshSettlementAttachments(settlementId: string) {
    const items = await listSettlementAttachments(groupId, settlementId);
    setSettlementAttachments(items);
  }

  useEffect(() => {
    if (!features.attachments) {
      setSettlementAttachmentsOpenId(null);
      setSettlementAttachments([]);
      return;
    }
    if (settlementAttachmentsOpenId) {
      refreshSettlementAttachments(settlementAttachmentsOpenId).catch(() => undefined);
    } else {
      setSettlementAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlementAttachmentsOpenId, features.attachments]);

  async function onUploadSettlementAttachment(settlementId: string, file: File) {
    if (file.type.startsWith("image/") && file.size > MAX_RECEIPT_IMAGE_BYTES) {
      throw new Error("Image too large (max 5 MB)");
    }
    await uploadSettlementAttachment(groupId, settlementId, file);
    await refreshSettlementAttachments(settlementId);
  }

  async function onUploadGroupAvatar(file: File) {
    setStatus(null);
    const upload = await uploadFile(file);
    setEditAvatarId(upload.id);
  }

  async function onSaveGroupDetails() {
    setStatus(null);
    await updateGroup(groupId, {
      name: editName,
      description: editDescription,
      avatar_attachment_id: editAvatarId,
    });
    await refreshAll();
    setEditMode(false);
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ ...cardStyle, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {group?.avatar_url ? (
            <AuthedImage
              src={group.avatar_url}
              alt="avatar"
              style={{ width: 46, height: 46, borderRadius: 16, objectFit: "cover" }}
            />
          ) : (
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 16,
                background: "#e8f3ec",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                color: "#0f8c57",
                fontSize: 18,
              }}
            >
              {(friendDisplayName || group?.name || "G").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <h2 style={{ margin: "0 0 2px 0", fontWeight: 800, fontSize: 20 }}>
              {friendDisplayName || group?.name || "Group"}
            </h2>
            <div style={subtleText}>
              {group?.currency_code ?? currency}
              {group?.kind && group.kind !== "friendship" ? ` · ${group.kind}` : ""}
              {group?.description ? ` · ${group.description}` : ""}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
          {isAdmin && (
            <button
              type="button"
              data-testid="group-action-edit"
              style={iconButton}
              title={t(lang, "editGroup")}
              aria-label={t(lang, "editGroup")}
              onClick={() => setEditMode(true)}
            >
              ✏️
            </button>
          )}
          <button
            type="button"
            data-testid="group-action-refresh"
            style={iconButton}
            title={t(lang, "refresh")}
            aria-label={t(lang, "refresh")}
            onClick={() => refreshAll()}
          >
            🔄
          </button>
          {group?.kind === "group" && (
            <button
              type="button"
              data-testid="group-action-invite"
              style={iconButton}
              title={t(lang, "createGroupInvite")}
              aria-label={t(lang, "createGroupInvite")}
              onClick={onCreateGroupInvite}
            >
              🔗
            </button>
          )}
          {group?.kind === "group" && isAdmin && (
            <button
              type="button"
              data-testid="group-action-add-member"
              style={iconButton}
              title={t(lang, "addMember")}
              aria-label={t(lang, "addMember")}
              onClick={openAddMemberDialog}
            >
              👥
            </button>
          )}
          <button
            type="button"
            data-testid="group-action-export"
            style={{
              ...iconButton,
              opacity: exporting || !expenses || !settlements ? 0.5 : 1,
              cursor: exporting || !expenses || !settlements ? "not-allowed" : "pointer",
            }}
            title={t(lang, "exportCsv")}
            aria-label={t(lang, "exportCsv")}
            onClick={() => exportCsv()}
            disabled={exporting || !expenses || !settlements}
          >
            ⬇️
          </button>
          {group?.kind === "group" && (
            <button
              type="button"
              data-testid="group-action-leave"
              style={{
                ...iconButton,
                border: "1px solid #f2d2c2",
                color: "#c65a2b",
                opacity: myNetMinor !== null && myNetMinor !== 0 ? 0.35 : 1,
                cursor: myNetMinor !== null && myNetMinor !== 0 ? "not-allowed" : "pointer",
              }}
              title={t(lang, "leaveGroup")}
              aria-label={t(lang, "leaveGroup")}
              onClick={() => onLeaveGroup().catch(() => undefined)}
            >
              🚪
            </button>
          )}
          <button
            type="button"
            data-testid="group-action-add-expense"
            style={{ ...iconButton, background: "var(--swk-green)", border: "1px solid var(--swk-green)", color: "#fff" }}
            title={t(lang, "addExpense")}
            aria-label={t(lang, "addExpense")}
            onClick={() => {
              setMode("new-expense");
              resetExpenseForm();
              resetSettlementForm();
            }}
          >
            ＋
          </button>
          <button
            type="button"
            data-testid="group-action-add-settlement"
            style={iconButton}
            title={t(lang, "addSettlement")}
            aria-label={t(lang, "addSettlement")}
            onClick={() => {
              setMode("new-settlement");
              resetSettlementForm();
            }}
          >
            💸
          </button>
        </div>
      </div>

      {inviteUrl && (
        <pre style={{ ...cardStyle, whiteSpace: "pre-wrap" }}>
          {t(lang, "joinUrl")}: {inviteUrl}
        </pre>
      )}

      {editMode && isAdmin && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 3000,
          }}
        >
          <div style={{ ...cardStyle, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>{t(lang, "groupDetails")}</h3>
              <button
                onClick={() => {
                  setEditMode(false);
                  setEditName(group?.name ?? "");
                  setEditDescription(group?.description ?? "");
                  setEditAvatarId(group?.avatar_attachment_id ?? null);
                }}
                style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer" }}
                aria-label={t(lang, "close")}
                title={t(lang, "close")}
                data-testid="group-edit-close"
              >
                ✕
              </button>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              <label>
                {t(lang, "name")}
                <input style={inputStyle} value={editName} onChange={(e) => setEditName(e.target.value)} />
              </label>
              <label>
                {t(lang, "description")}
                <textarea
                  style={{ ...inputStyle, resize: "vertical" }}
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </label>
              <div>
                <div style={{ marginBottom: 6 }}>{t(lang, "avatar")}</div>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  {group?.avatar_url && (
                    <AuthedImage
                      src={group.avatar_url}
                      alt="avatar"
                      style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover" }}
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onUploadGroupAvatar(f).catch((err) => setStatus(String(err)));
                    }}
                  />
                  {editAvatarId && <span style={{ fontSize: 12, color: "#666" }}>{t(lang, "avatarReady")}</span>}
                  {editAvatarId && (
                    <button
                      type="button"
                      onClick={() => setEditAvatarId(null)}
                      style={{ padding: "4px 8px" }}
                    >
                      {t(lang, "removeAvatar")}
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button style={primaryButton} onClick={() => onSaveGroupDetails().catch((err) => setStatus(String(err)))}>
                  {t(lang, "saveChanges")}
                </button>
                <button
                  style={outlineButton}
                  type="button"
                  onClick={() => {
                    setEditMode(false);
                    setEditName(group?.name ?? "");
                    setEditDescription(group?.description ?? "");
                    setEditAvatarId(group?.avatar_attachment_id ?? null);
                  }}
                >
                  {t(lang, "cancel")}
                </button>
              </div>

              <div style={{ borderTop: "1px solid var(--swk-border)", paddingTop: 10, marginTop: 6 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                  <div style={{ fontWeight: 900 }}>{t(lang, "members")}</div>
                  {group?.kind === "group" && isAdmin && (
                    <button
                      type="button"
                      style={smallButton}
                      onClick={openAddMemberDialog}
                      data-testid="group-member-add-open"
                    >
                      {t(lang, "addMember")}
                    </button>
                  )}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {(members ?? []).map((m) => {
                    const net = netByUserId.get(m.user_id) ?? 0;
                    const canRemove = m.user_id !== meId && net === 0;
                    return (
                      <div
                        key={m.user_id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid var(--swk-border)",
                          background: "var(--swk-input-bg)",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {m.display_name || m.email}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>
                            {m.role}
                            {net !== 0 ? ` · ${formatMoney(net, currency)}` : ""}
                          </div>
                        </div>
                        {m.user_id !== meId && (
                          <button
                            type="button"
                            onClick={() => onRemoveMember(m.user_id).catch(() => undefined)}
                            disabled={!canRemove}
                            data-testid={`member-remove-${m.user_id}`}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#c65a2b",
                              fontSize: 18,
                              cursor: canRemove ? "pointer" : "not-allowed",
                              opacity: canRemove ? 1 : 0.35,
                            }}
                            title={t(lang, "removeMember")}
                            aria-label={t(lang, "removeMember")}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ borderTop: "1px solid var(--swk-border)", paddingTop: 10 }}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>{t(lang, "invites")}</div>
                <div style={{ display: "grid", gap: 8 }}>
                  {(invites ?? []).length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>{t(lang, "none")}</div>
                  ) : (
                    (invites ?? []).map((inv) => {
                      const statusLabel = inv.revoked_at ? t(lang, "revoked") : inv.used_at ? t(lang, "used") : t(lang, "active");
                      const canRevoke = !inv.revoked_at && !inv.used_at;
                      return (
                        <div
                          key={inv.token}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                            padding: "10px 12px",
                            borderRadius: 12,
                            border: "1px solid var(--swk-border)",
                            background: "var(--swk-input-bg)",
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 800, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {inv.url || inv.token}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>{statusLabel}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => onRevokeInvite(inv.token).catch(() => undefined)}
                            disabled={!canRevoke}
                            data-testid={`invite-revoke-${inv.token}`}
                            style={{
                              padding: "8px 10px",
                              borderRadius: 10,
                              border: canRevoke ? "1px solid #c65a2b" : "1px solid var(--swk-border)",
                              background: "transparent",
                              color: canRevoke ? "#c65a2b" : "var(--swk-muted)",
                              fontWeight: 800,
                              cursor: canRevoke ? "pointer" : "not-allowed",
                              opacity: canRevoke ? 1 : 0.5,
                            }}
                          >
                            {t(lang, "revoke")}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddMember && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 3200,
          }}
        >
          <div
            style={{
              background: "var(--swk-card)",
              borderRadius: 14,
              padding: 16,
              width: "100%",
              maxWidth: 520,
              boxShadow: "0 16px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontWeight: 900, fontSize: 18 }}>{t(lang, "addMember")}</div>
              <button
                onClick={() => setShowAddMember(false)}
                style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "var(--swk-input-text)" }}
                aria-label={t(lang, "close")}
                title={t(lang, "close")}
              >
                ✕
              </button>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => {
                  setAddMemberMode("contacts");
                  setAddMemberSearch("");
                  ensureContactsLoaded().catch(() => undefined);
                }}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid var(--swk-border)",
                  background: addMemberMode === "contacts" ? "rgba(15,140,87,0.15)" : "var(--swk-input-bg)",
                  color: "var(--swk-input-text)",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {t(lang, "fromContacts")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddMemberMode("email");
                  setAddMemberSearch("");
                }}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid var(--swk-border)",
                  background: addMemberMode === "email" ? "rgba(15,140,87,0.15)" : "var(--swk-input-bg)",
                  color: "var(--swk-input-text)",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {t(lang, "byEmail")}
              </button>
            </div>

            {addMemberMode === "contacts" ? (
              <div style={{ display: "grid", gap: 10 }}>
                <input
                  className="swk-input"
                  style={inputStyle}
                  value={addMemberSearch}
                  onChange={(e) => setAddMemberSearch(e.target.value)}
                  placeholder={t(lang, "search")}
                />
                {contactsStatus && <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>{String(contactsStatus)}</div>}
                {eligibleContacts.length === 0 ? (
                  <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>{t(lang, "noEligibleContacts")}</div>
                ) : (
                  <div style={{ maxHeight: 280, overflow: "auto", border: "1px solid var(--swk-border)", borderRadius: 12 }}>
                    {eligibleContacts.map((c) => (
                      <label
                        key={c.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 12px",
                          borderBottom: "1px solid var(--swk-border)",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(c.contact_user_id && selectedContactUserIds[c.contact_user_id])}
                          onChange={(e) => {
                            if (!c.contact_user_id) return;
                            setSelectedContactUserIds({ ...selectedContactUserIds, [c.contact_user_id]: e.target.checked });
                          }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.resolved_display_name}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>{c.email || c.phone || ""}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontSize: 12, color: "var(--swk-muted)", fontWeight: 800 }}>{t(lang, "email")}</span>
                  <input
                    className="swk-input"
                    style={inputStyle}
                    value={addMemberEmail}
                    onChange={(e) => setAddMemberEmail(e.target.value)}
                    placeholder="name@example.com"
                    autoComplete="email"
                  />
                </label>
                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontSize: 12, color: "var(--swk-muted)", fontWeight: 800 }}>{t(lang, "displayNameOptional")}</span>
                  <input
                    className="swk-input"
                    style={inputStyle}
                    value={addMemberDisplayName}
                    onChange={(e) => setAddMemberDisplayName(e.target.value)}
                    placeholder={t(lang, "displayNameOptional")}
                  />
                </label>
                <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>{t(lang, "placeholderHint")}</div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
              <button type="button" style={outlineButton} onClick={() => setShowAddMember(false)} disabled={addingMember}>
                {t(lang, "cancel")}
              </button>
              <button
                type="button"
                style={primaryButton}
                onClick={() => onAddMembersToGroup().catch(() => undefined)}
                disabled={addingMember}
                data-testid="group-member-add-submit"
              >
                {addingMember ? t(lang, "saving") : t(lang, "add")}
              </button>
            </div>
          </div>
        </div>
      )}

      {group?.kind !== "friendship" && (
        <>
          <h3 style={{ marginTop: 8, color: "#0f8c57" }}>{t(lang, "members")}</h3>
          <div style={cardStyle}>
            {(members ?? []).length === 0 ? (
              <div style={{ color: "#666" }}>{t(lang, "none")}</div>
            ) : (
              <ul>
                {(members ?? []).map((m) => (
                  <li key={m.user_id}>
                    {(m.display_name || m.email)} — {t(lang, "role")}: {m.role}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {status && (
        <pre style={{ ...cardStyle, whiteSpace: "pre-wrap", color: "#b00", background: "#fff3f3" }}>
          {status}
        </pre>
      )}

      {mode === "new-expense" || mode === "edit-expense" ? (
        <div data-testid={mode === "edit-expense" ? "expense-edit-form" : "expense-new-form"} style={cardStyle}>
          <h3>{mode === "edit-expense" ? t(lang, "editExpense") : t(lang, "newExpense")}</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setStatus(null);
              submitExpense(mode === "edit-expense").catch((err) => setStatus(String(err)));
            }}
            style={{ display: "grid", gap: 10 }}
          >
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "description")}
              <input style={inputStyle} value={expenseDescription} onChange={(e) => setExpenseDescription(e.target.value)} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "currency")}
              <select
                data-testid="expense-currency-select"
                style={inputStyle}
                value={expenseCurrencyCode}
                onChange={(e) => setExpenseCurrencyCode(e.target.value)}
              >
                {availableCurrencyOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "amount")} ({expenseCurrencyCode})
              <input style={inputStyle} value={expenseAmountMajor} onChange={(e) => setExpenseAmountMajor(e.target.value)} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "when")}
              <input
                style={inputStyle}
                type="datetime-local"
                value={expenseDatetimeLocal}
                onChange={(e) => setExpenseDatetimeLocal(e.target.value)}
              />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "category")}
              <select style={inputStyle} value={expenseCategoryId} onChange={(e) => setExpenseCategoryId(e.target.value)}>
                <option value="">{t(lang, "none")}</option>
                {(categories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                style={{ ...inputStyle, flex: "1 1 260px" }}
                placeholder={t(lang, "category")}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
              />
              <button
                style={outlineButton}
                type="button"
                onClick={() => {
                  setStatus(null);
                  createCategory(newCategoryName.trim())
                    .then(async (c) => {
                      setNewCategoryName("");
                      setExpenseCategoryId(c.id);
                      setCategories(await listCategories());
                    })
                    .catch((err) => setStatus(String(err)));
                }}
                disabled={!newCategoryName.trim()}
              >
                {t(lang, "create")}
              </button>
            </div>
            <label>
              {t(lang, "note")}
              <textarea style={{ ...inputStyle, resize: "vertical" }} value={expenseNote} onChange={(e) => setExpenseNote(e.target.value)} />
            </label>

            {features.attachments ? (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontWeight: 900 }}>{t(lang, "attachments")}</div>
                <div style={subtleText}>Images up to 5 MB. Server will compress to JPEG.</div>
                <input
                  type="file"
                  accept="image/*"
                  disabled={isUploadingExpenseAttachment}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onUploadAttachment(f).catch((err) => setStatus(String(err)));
                    e.currentTarget.value = "";
                  }}
                />

                {mode === "new-expense" && pendingNewExpenseAttachment ? (
                  <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>Selected: {pendingNewExpenseAttachment.name}</div>
                ) : null}

                {mode === "edit-expense" ? (
                  attachments.length ? (
                    <ul style={{ margin: 0 }}>
                      {attachments.map((a) => (
                        <li key={a.id}>
                          <button
                            type="button"
                            onClick={() =>
                              openAuthedFile(a.download_url, a.original_filename ?? undefined).catch((err) => setStatus(String(err)))
                            }
                            style={{ border: "none", background: "transparent", color: "#0f8c57", fontWeight: 700, padding: 0 }}
                          >
                            {a.original_filename ?? a.attachment_id}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div style={subtleText}>{t(lang, "none")}</div>
                  )
                ) : null}
              </div>
            ) : (
              <div style={{ color: "#666" }}>{t(lang, "disabled")}</div>
            )}

            <div>
              <h4>{t(lang, "payers")}</h4>
              {amountMinor > 0 && splitType !== "equal" && (
                <div style={subtleText}>
                  {t(lang, "remainingToAssign")}: {formatMoney(payerRemaining, expenseCurrencyCode)}
                </div>
              )}
              {splitType === "equal" && <div style={subtleText}>{t(lang, "autoForEqualPayers")}</div>}
              {(members ?? []).map((m) =>
                splitType === "equal" ? (
                  <label key={m.user_id} style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 8, alignItems: "center" }}>
                    <input
                      type="checkbox"
                      data-testid={`equal-payer-${m.user_id}`}
                      checked={equalPayers[m.user_id] ?? false}
                      onChange={(e) => setEqualPayers({ ...equalPayers, [m.user_id]: e.target.checked })}
                    />
                    <span>{m.display_name || m.email}</span>
                    <span style={{ ...subtleText, fontWeight: 800 }}>{formatMoney(equalAutoPayerMinorByUser[m.user_id] ?? 0, expenseCurrencyCode)}</span>
                  </label>
                ) : (
                  <label key={m.user_id} style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 8, alignItems: "center" }}>
                    <span>{m.display_name || m.email}</span>
                    <input
                      data-testid={`payer-amount-${m.user_id}`}
                      style={inputStyle}
                      value={payerAmounts[m.user_id] ?? "0"}
                      onChange={(e) => setPayerAmounts({ ...payerAmounts, [m.user_id]: e.target.value })}
                    />
                  </label>
                ),
              )}
            </div>

            <div>
              <h4>{t(lang, "splitType")}</h4>
              <select style={inputStyle} value={splitType} onChange={(e) => setSplitType(e.target.value as SplitType)}>
                <option value="equal">{t(lang, "equal")}</option>
                <option value="exact">{t(lang, "exact")}</option>
                <option value="percent">{t(lang, "percent")}</option>
                <option value="shares">{t(lang, "shares")}</option>
              </select>
            </div>

            {splitType === "equal" && group?.kind !== "friendship" && (
              <div>
                <h4>{t(lang, "participants")}</h4>
                {(members ?? []).map((m) => (
                  <label key={m.user_id} style={{ display: "block" }}>
                    <input
                      data-testid={`equal-participant-${m.user_id}`}
                      type="checkbox"
                      checked={equalParticipants[m.user_id] ?? true}
                      onChange={(e) => setEqualParticipants({ ...equalParticipants, [m.user_id]: e.target.checked })}
                    />{" "}
                    {m.display_name || m.email}
                  </label>
                ))}
              </div>
            )}

          {splitType === "exact" && (
            <div>
              <h4>{t(lang, "exactAmounts")}</h4>
              {amountMinor > 0 && (
                <div style={subtleText}>
                  {t(lang, "remainingToAssign")}: {formatMoney(exactRemaining, expenseCurrencyCode)}
                </div>
              )}
              {(members ?? []).map((m) => (
                <label key={m.user_id} style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 8, alignItems: "center" }}>
                  <span>{m.display_name || m.email}</span>
                  <input
                    style={inputStyle}
                    value={exactAmounts[m.user_id] ?? "0"}
                    onChange={(e) => setExactAmounts({ ...exactAmounts, [m.user_id]: e.target.value })}
                  />
                </label>
                ))}
              </div>
            )}

            {splitType === "percent" && (
              <div>
                <h4>{t(lang, "percentSum")}</h4>
              {(members ?? []).map((m) => (
                <label key={m.user_id} style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 8, alignItems: "center" }}>
                  <span>{m.display_name || m.email}</span>
                  <input
                    style={inputStyle}
                    value={percentValues[m.user_id] ?? "0"}
                    onChange={(e) => setPercentValues({ ...percentValues, [m.user_id]: e.target.value })}
                  />
                </label>
                ))}
              </div>
            )}

            {splitType === "shares" && (
              <div>
                <h4>{t(lang, "sharesSum")}</h4>
              {(members ?? []).map((m) => (
                <label key={m.user_id} style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 8, alignItems: "center" }}>
                  <span>{m.display_name || m.email}</span>
                  <input
                    style={inputStyle}
                    value={sharesValues[m.user_id] ?? "0"}
                    onChange={(e) => setSharesValues({ ...sharesValues, [m.user_id]: e.target.value })}
                  />
                </label>
                ))}
              </div>
            )}

            {mode === "edit-expense" && features.comments ? (
              <details style={{ border: "1px solid var(--swk-border)", borderRadius: 12, padding: 10 }}>
                <summary style={{ fontWeight: 900, cursor: "pointer" }}>{t(lang, "comments")}</summary>
                <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                  <div style={{ display: "grid", gap: 8 }}>
                    <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder={t(lang, "writeComment")} />
                    <button onClick={() => onAddComment().catch((err) => setStatus(String(err)))} disabled={!newComment.trim()}>
                      {t(lang, "addComment")}
                    </button>
                  </div>

                  <ul style={{ margin: 0 }}>
                    {(activeExpense?.comments ?? []).map((c) => (
                      <li key={c.id} style={{ marginBottom: 10 }}>
                        <div>
                          <b>{label(c.user_id)}</b> {c.is_deleted ? "(deleted)" : ""}
                        </div>
                        {editCommentId === c.id ? (
                          <div style={{ display: "grid", gap: 8 }}>
                            <textarea value={editCommentBody} onChange={(e) => setEditCommentBody(e.target.value)} />
                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={() => onSaveComment().catch((err) => setStatus(String(err)))}>
                                {t(lang, "save")}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditCommentId(null);
                                  setEditCommentBody("");
                                }}
                              >
                                {t(lang, "cancel")}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ whiteSpace: "pre-wrap" }}>{c.body}</div>
                        )}
                        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                          {!c.is_deleted ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditCommentId(c.id);
                                  setEditCommentBody(c.body);
                                }}
                              >
                                {t(lang, "edit")}
                              </button>
                              <button type="button" onClick={() => onDeleteComment(c.id).catch((err) => setStatus(String(err)))}>
                                {t(lang, "delete")}
                              </button>
                            </>
                          ) : (
                            <button type="button" onClick={() => onRestoreComment(c.id).catch((err) => setStatus(String(err)))}>
                              {t(lang, "restore")}
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            ) : null}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button data-testid="expense-form-save" style={primaryButton} type="submit">
                {mode === "edit-expense" ? t(lang, "save") : t(lang, "create")}
              </button>
              <button
                data-testid="expense-form-cancel"
                style={outlineButton}
                type="button"
                onClick={() => {
                  setMode("view");
                  setActiveExpenseId(null);
                  resetExpenseForm();
                }}
              >
                {t(lang, "cancel")}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {mode === "new-settlement" || mode === "edit-settlement" ? (
        <div data-testid={mode === "edit-settlement" ? "settlement-edit-form" : "settlement-new-form"} style={cardStyle}>
          <h3>{mode === "edit-settlement" ? t(lang, "edit") : t(lang, "newSettlement")}</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setStatus(null);
              submitSettlement(mode === "edit-settlement").catch((err) => setStatus(String(err)));
            }}
            style={{ display: "grid", gap: 10 }}
          >
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "from")}
              <select style={inputStyle} value={settlementFrom} onChange={(e) => setSettlementFrom(e.target.value)}>
                <option value="">{t(lang, "none")}</option>
                {(members ?? []).map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.display_name || m.email}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "to")}
              <select style={inputStyle} value={settlementTo} onChange={(e) => setSettlementTo(e.target.value)}>
                <option value="">{t(lang, "none")}</option>
                {(members ?? []).map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.display_name || m.email}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "currency")}
              <select
                data-testid="settlement-currency-select"
                style={inputStyle}
                value={settlementCurrencyCode}
                onChange={(e) => setSettlementCurrencyCode(e.target.value)}
              >
                {availableCurrencyOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "amount")} ({settlementCurrencyCode})
              <input style={inputStyle} value={settlementAmountMajor} onChange={(e) => setSettlementAmountMajor(e.target.value)} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              {t(lang, "note")}
              <input
                data-testid="settlement-note-input"
                style={inputStyle}
                value={settlementNote}
                onChange={(e) => setSettlementNote(e.target.value)}
              />
            </label>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button data-testid="settlement-form-save" style={primaryButton} type="submit">
                {mode === "edit-settlement" ? t(lang, "save") : t(lang, "create")}
              </button>
              <button
                data-testid="settlement-form-cancel"
                style={outlineButton}
                type="button"
                onClick={() => {
                  setMode("view");
                  resetSettlementForm();
                }}
              >
                {t(lang, "cancel")}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <h3 style={{ marginTop: 8, color: "#0f8c57" }}>{t(lang, "balances")}</h3>
      {balances && (
        <div style={cardStyle}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
            {balances.balances.map((b) => (
              <li key={b.user_id} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {label(b.user_id)}
                </span>
                <b style={{ textAlign: "right", whiteSpace: "nowrap" }}>{formatMoney(b.net_minor, balances.currency_code)}</b>
              </li>
            ))}
          </ul>
          {features.debt_simplification ? (
            <>
              <h4>{t(lang, "simplifiedTransfers")}</h4>
              {balances.simplified.length === 0 ? (
                <div style={{ color: "#666" }}>{t(lang, "none")}</div>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
                  {balances.simplified.map((t, idx) => (
                    <li key={idx} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {label(t.from_user_id)} → {label(t.to_user_id)}
                      </span>
                      <span style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 800 }}>
                        {formatMoney(t.amount_minor, balances.currency_code)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div style={{ color: "#666" }}>{t(lang, "disabled")}</div>
          )}
        </div>
      )}

      <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, color: "#0f8c57" }}>{t(lang, "expenses")}</h3>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} />
          <span style={{ fontWeight: 800, color: "var(--swk-muted)", fontSize: 12 }}>{t(lang, "includeDeleted")}</span>
        </label>
      </div>
      {expenses && (
        <div style={cardStyle}>
          <ul>
            {expenses.map((e) => (
              <li key={e.id} style={{ marginBottom: 10 }}>
                {isMobileTouch ? (
                  <SwipeRow
                    id={e.id}
                    testId={`swipe-expense-${e.id}`}
                    openSide={openExpenseSwipe?.id === e.id ? openExpenseSwipe.side : null}
                    onOpenSideChange={(id, side) => setOpenExpenseSwipe(side ? { id, side } : null)}
                    leftActions={
                      <button
                        type="button"
                        data-testid={`expense-edit-${e.id}`}
                        style={smallButton}
                        onClick={() => {
                          setOpenExpenseSwipe(null);
                          beginEditExpense(e);
                        }}
                      >
                        {t(lang, "edit")}
                      </button>
                    }
                    rightActions={
                      !e.is_deleted ? (
                        <button
                          type="button"
                          data-testid={`expense-delete-${e.id}`}
                          style={smallDangerButton}
                          onClick={() => {
                            setOpenExpenseSwipe(null);
                            onDeleteExpense(e.id).catch(() => undefined);
                          }}
                        >
                          {t(lang, "delete")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          data-testid={`expense-restore-${e.id}`}
                          style={smallButton}
                          onClick={() => {
                            setOpenExpenseSwipe(null);
                            onRestoreExpense(e.id).catch(() => undefined);
                          }}
                        >
                          {t(lang, "restore")}
                        </button>
                      )
                    }
                  >
                    <div data-testid={`expense-row-${e.id}`} onClick={() => beginEditExpense(e)} style={{ cursor: "pointer" }}>
                      <b style={{ color: "var(--swk-input-text)" }}>{e.description}</b> — {formatMoney(e.amount_minor, e.currency_code)}{" "}
                      {e.is_deleted ? t(lang, "deleted") : ""}
                      <div style={{ color: "#666", marginTop: 4 }}>
                        {t(lang, "paidBy")}{" "}
                        {e.payers.map((p) => `${label(p.user_id)} ${formatMoney(p.amount_minor, e.currency_code)}`).join(", ")}
                      </div>
                      <div style={{ color: "#666", marginTop: 2 }}>
                        {new Date(e.occurred_at).toLocaleString()}
                        {e.category_id ? ` · ${categoryById.get(e.category_id)?.name ?? e.category_id}` : ""}
                      </div>
                    </div>
                  </SwipeRow>
                ) : (
                  <>
                    <button onClick={() => beginEditExpense(e)} style={{ ...smallButton, marginRight: 8 }}>
                      {t(lang, "edit")}
                    </button>
                    {!e.is_deleted ? (
                      <button onClick={() => onDeleteExpense(e.id)} style={{ ...smallDangerButton, marginRight: 8 }}>
                        {t(lang, "delete")}
                      </button>
                    ) : (
                      <button onClick={() => onRestoreExpense(e.id)} style={{ ...smallButton, marginRight: 8 }}>
                        {t(lang, "restore")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => beginEditExpense(e)}
                      style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
                      title={t(lang, "edit")}
                      aria-label={t(lang, "edit")}
                    >
                      <b style={{ color: "var(--swk-input-text)" }}>{e.description}</b>
                    </button>{" "}
                    — {formatMoney(e.amount_minor, e.currency_code)} {e.is_deleted ? t(lang, "deleted") : ""}
                    <div style={{ color: "#666", marginTop: 4 }}>
                      {t(lang, "paidBy")}{" "}
                      {e.payers.map((p) => `${label(p.user_id)} ${formatMoney(p.amount_minor, e.currency_code)}`).join(", ")}
                    </div>
                    <div style={{ color: "#666", marginTop: 2 }}>
                      {new Date(e.occurred_at).toLocaleString()}
                      {e.category_id ? ` · ${categoryById.get(e.category_id)?.name ?? e.category_id}` : ""}
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, color: "#0f8c57" }}>{t(lang, "settlements")}</h3>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} />
          <span style={{ fontWeight: 800, color: "var(--swk-muted)", fontSize: 12 }}>{t(lang, "includeDeleted")}</span>
        </label>
      </div>
      {settlements && (
        <div style={cardStyle}>
          <ul>
            {settlements.map((s) => (
              <li key={s.id} style={{ marginBottom: 10 }}>
                {isMobileTouch ? (
                  <SwipeRow
                    id={s.id}
                    testId={`swipe-settlement-${s.id}`}
                    openSide={openSettlementSwipe?.id === s.id ? openSettlementSwipe.side : null}
                    onOpenSideChange={(id, side) => setOpenSettlementSwipe(side ? { id, side } : null)}
                    leftActions={
                      <button
                        type="button"
                        data-testid={`settlement-edit-${s.id}`}
                        style={smallButton}
                        onClick={() => {
                          setOpenSettlementSwipe(null);
                          beginEditSettlement(s);
                        }}
                      >
                        {t(lang, "edit")}
                      </button>
                    }
                    rightActions={
                      !s.is_deleted ? (
                        <button
                          type="button"
                          data-testid={`settlement-delete-${s.id}`}
                          style={smallDangerButton}
                          onClick={() => {
                            setOpenSettlementSwipe(null);
                            onDeleteSettlement(s.id).catch(() => undefined);
                          }}
                        >
                          {t(lang, "delete")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          data-testid={`settlement-restore-${s.id}`}
                          style={smallButton}
                          onClick={() => {
                            setOpenSettlementSwipe(null);
                            onRestoreSettlement(s.id).catch(() => undefined);
                          }}
                        >
                          {t(lang, "restore")}
                        </button>
                      )
                    }
                  >
                    <div data-testid={`settlement-row-${s.id}`}>
                      {label(s.from_user_id)} → {label(s.to_user_id)}: {formatMoney(s.amount_minor, s.currency_code)}{" "}
                      {s.is_deleted ? t(lang, "deleted") : ""}
                      {features.attachments ? (
                        <button
                          type="button"
                          style={{ ...smallButton, marginLeft: 8 }}
                          onClick={() => setSettlementAttachmentsOpenId(settlementAttachmentsOpenId === s.id ? null : s.id)}
                        >
                          {t(lang, "attachments")}
                        </button>
                      ) : null}
                      {s.note ? (
                        <div style={{ color: "#666", marginTop: 4 }}>
                          {t(lang, "noteLabel")}: {s.note}
                        </div>
                      ) : null}
                      {features.attachments && settlementAttachmentsOpenId === s.id ? (
                        <div style={{ marginTop: 8 }}>
                          <input
                            type="file"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) onUploadSettlementAttachment(s.id, f).catch((err) => setStatus(String(err)));
                              e.currentTarget.value = "";
                            }}
                          />
                          <ul style={{ marginTop: 8 }}>
                            {settlementAttachments.map((a) => (
                              <li key={a.id}>
                                <button
                                  type="button"
                                  onClick={() =>
                                    openAuthedFile(a.download_url, a.original_filename ?? undefined).catch((err) => setStatus(String(err)))
                                  }
                                  style={{ border: "none", background: "transparent", color: "#0f8c57", fontWeight: 700, padding: 0 }}
                                >
                                  {a.original_filename ?? a.attachment_id}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  </SwipeRow>
                ) : (
                  <>
                    <button onClick={() => beginEditSettlement(s)} style={{ ...smallButton, marginRight: 8 }}>
                      {t(lang, "edit")}
                    </button>
                    {!s.is_deleted ? (
                      <button onClick={() => onDeleteSettlement(s.id)} style={{ ...smallDangerButton, marginRight: 8 }}>
                        {t(lang, "delete")}
                      </button>
                    ) : (
                      <button onClick={() => onRestoreSettlement(s.id)} style={{ ...smallButton, marginRight: 8 }}>
                        {t(lang, "restore")}
                      </button>
                    )}
                    {label(s.from_user_id)} → {label(s.to_user_id)}: {formatMoney(s.amount_minor, s.currency_code)}{" "}
                    {s.is_deleted ? t(lang, "deleted") : ""}
                    {features.attachments ? (
                      <button
                        type="button"
                        style={{ ...smallButton, marginLeft: 8 }}
                        onClick={() => setSettlementAttachmentsOpenId(settlementAttachmentsOpenId === s.id ? null : s.id)}
                      >
                        {t(lang, "attachments")}
                      </button>
                    ) : null}
                    {s.note ? (
                      <div style={{ color: "#666", marginTop: 4 }}>
                        {t(lang, "noteLabel")}: {s.note}
                      </div>
                    ) : null}
                    {features.attachments && settlementAttachmentsOpenId === s.id ? (
                      <div style={{ marginTop: 8 }}>
                        <input
                          type="file"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) onUploadSettlementAttachment(s.id, f).catch((err) => setStatus(String(err)));
                            e.currentTarget.value = "";
                          }}
                        />
                        <ul style={{ marginTop: 8 }}>
                          {settlementAttachments.map((a) => (
                            <li key={a.id}>
                              <button
                                type="button"
                                onClick={() =>
                                  openAuthedFile(a.download_url, a.original_filename ?? undefined).catch((err) => setStatus(String(err)))
                                }
                                style={{ border: "none", background: "transparent", color: "#0f8c57", fontWeight: 700, padding: 0 }}
                              >
                                {a.original_filename ?? a.attachment_id}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
